'use client'

import Image from "next/image";
import Link from "@/components/ui/Link";
import { sanitizeHTML } from "@/lib/utils";
import { getReadableImageUrl } from "@/lib/directus";
import { useEffect, useRef, useState } from "react";

interface WhatWeOfferData {
  subheading?: {
    text?: string;
    title?: string;
  };
  heading?: {
    text?: string;
    title?: string;
  };
  text?: {
    text?: string;
  };
  content_collection?: {
    id: string;
    site_content_collections_id: {
      collection: {
        collection: string;
        item: {
          title?: string;
          text?: string;
          href?: string;
          label?: string;
          alt?: string;
          image_file?: {
            image: {
              id: string;
              filename_download: string;
            };
          };
        };
      }[];
    };
  }[];
}

export const fields = [
  "subheading.text",
  "subheading.title",
  "heading.text",
  "heading.title",
  "text.text",
  "content_collection.id",
  "content_collection.site_content_collections_id.collection.collection",
  "content_collection.site_content_collections_id.collection.item:site_content.title",
  "content_collection.site_content_collections_id.collection.item:site_content.text",
  "content_collection.site_content_collections_id.collection.item:images.alt",
  "content_collection.site_content_collections_id.collection.item:images.image_file.image.id",
  "content_collection.site_content_collections_id.collection.item:images.image_file.image.filename_download",
  "content_collection.site_content_collections_id.collection.item:menu_items.href",
  "content_collection.site_content_collections_id.collection.item:menu_items.label",
] as const;

interface WhatWeOfferProps {
  data?: WhatWeOfferData;
}

export default function WhatWeOffer({ data }: WhatWeOfferProps) {
  const sectionRef = useRef<HTMLElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.05, rootMargin: "0px" }
    );
    const current = sectionRef.current;
    if (current) observer.observe(current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} id="WhatWeOffer" className="py-16 bg-[#f8f8f8]">
      <div className="container">
        <div
          className={`text-center mb-12 md:mb-16 transition-all duration-700 ease-out ${
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
          }`}
        >
          {data?.subheading && (
            <div className="font-aileron font-bold text-sm uppercase tracking-wider text-secondary-dark mb-4">
              {data.subheading.title || data.subheading.text}
            </div>
          )}
          {data?.heading && (
            <h2>
              {data.heading.title || data.heading.text}
            </h2>
          )}
          {data?.text?.text && (
            <div
              className="md:[&>p]:text-xl leading-7 tracking-tight text-[#212529] max-w-4xl mx-auto prose prose-p:mb-0"
              dangerouslySetInnerHTML={sanitizeHTML(data.text.text)}
            />
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-10">
          {data?.content_collection?.map((collection, index) => {
            const coll = collection?.site_content_collections_id?.collection || [];
            const imageItem = coll.find((c) => c.collection === "images")?.item;
            const titleItem = coll.find(
              (c) => c.collection === "site_content" && c.item?.title
            )?.item;
            const textItem = coll.find(
              (c) => c.collection === "site_content" && c.item?.text
            )?.item;
            const menuItem = coll.find((c) => c.collection === "menu_items")?.item;

            const imageId = imageItem?.image_file?.image?.id || "";
            const imageFilename = imageItem?.image_file?.image?.filename_download || "";
            const imageAlt = imageItem?.alt || "";
            const title = titleItem?.title || "";
            const text = textItem?.text || titleItem?.text || "";
            const href = menuItem?.href || "#";
            const label = menuItem?.label || "";

            return (
							<div
								key={collection.id}
								className={`bg-white rounded-lg shadow-md overflow-hidden flex flex-col 2xl:flex-row transition-all duration-700 ease-out ${
									isVisible ? "opacity-100 scale-100" : "opacity-0 scale-90"
								}`}
								style={{
									transitionDelay: isVisible ? `${index * 100}ms` : "0ms",
								}}
							>
								<div className='relative w-full 2xl:w-[50%] 3xl:w-[38%] min-h-[250px] 3xl:min-h-[400px] 2xl:order-1 shrink-0'>
									{imageId && imageFilename && (
										<Image
											src={getReadableImageUrl(imageId, imageFilename)}
											alt={imageAlt}
											fill
											className='object-cover object-center'
											quality={95}
											loading='lazy'
											sizes='(min-width: 768px) 40vw, 100vw'
										/>
									)}
								</div>
								<div className='flex flex-col justify-between p-6 md:p-8 2xl:w-[50%] 3xl:w-[62%] 2xl:order-2 flex-1 min-w-0'>
									<div>
										<h3 className='md:text-4xl font-freightBig text-[#332F2C] mb-4'>
											{title}
										</h3>
										<div
											className='prose prose-p:mb-3 prose-ul:my-4 prose-li:py-1 [&_ul]:grid 3xl:[&_ul]:grid-cols-2 [&_ul]:gap-x-4'
											dangerouslySetInnerHTML={sanitizeHTML(text)}
										/>
									</div>
									{label && (
										<div className='mt-6'>
											<Link href={href} className='btn inline-block w-fit'>
												{label}
											</Link>
										</div>
									)}
								</div>
							</div>
						)
          })}
        </div>
      </div>
    </section>
  );
}
